import React, { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter as Router, Link, Route, Switch, Redirect, useHistory, useLocation } from "react-router-dom";
import { Button, Col, Container, Modal, Nav, NavDropdown, Row, Spinner } from "react-bootstrap";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import Alert from "react-bootstrap/Alert";
import { MdSearch, MdOutlineWarningAmber, MdErrorOutline, MdCheckCircleOutline, MdInfoOutline, MdMenu, MdClose } from "react-icons/md";
import { BsChevronRight } from "react-icons/bs";
import "./Navigation.css";

import SaluteCard from "./Common/HomeWidgets/Widgets/Salute/SaluteCard";
import LabResult from "./LabResult";
import UserResult from "./UserResult/UserResult";
import UserRequest from "./UserRequest/UserRequest";
import LabRequest from "./LabRequest";
import ReportOrders from "./ReportOrders";
import MessageView from "./MessageView";
import Profile_deprecated from "./Profile_deprecated";
import Profile, { profileRedirectReason } from "./Profile";
import BookingList from "./BookingList";
import BookingCalendar from "./BookingCalendar";
import BookingDetails from "./BookingDetails";
import KitDetailsPage from "./KitDetailsPage";
import PatientProfile from "./PatientProfileV2/PatientProfilePage";
import PatientSearchPage from "./PatientSearchPage";
import OrderKitLookupPage from "./OrderKitLookupPage";
import CollectPayment from "./CollectPayment";
import Inbox from "./MessageIndexInbox";
import ActiveCases from "./HomeTable/ActiveCases";
import WidgetRow from "./Common/HomeWidgets/WidgetRow/WidgetRow";
import Dashboard from "./Dashboard";
import ManageLabOrder from "./MangeLabOrder";
import ShippingManifest from "./ShippingManifest";
import Unauthorized from "./Unauthorized";
import AuthWrapper from "./Auth/AuthWrapper";
import PrivateRoute from "./PrivateRoute";
import AdminDashboard from "./SuperAdminPanel/Dashboard";
import PrepDashboard from "./SuperAdminPanel/PrepDashboard";

import ApiService from "../ApiService";
import { Auth } from "@aws-amplify/auth";
import { clinicianIdFromRefreshAuth, decodeIdToken, getUserRoles, ROLE_NAME, CLINICIAN_STATUS } from "../util/authUtil";
import { parseAxiosError } from "../util/parserHelper";
import url, { NAV_LABELS } from "../util/routeURL";
import removeClass from "../util/RemoveCSS";
import logo from "../assets/animated_logo1.gif";
import { clearCognitoCookies } from "../util/clearCognitoCookies";

// Alert component for snackbars
function SnackStyleAlert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// Modal to prompt users for license info if needed
function OnboardingModalOne({ show, onHide }) {
  const message = "Before getting started, you'll need to enter your clinician licensing information.";
  return (
    <Modal show={show} onHide={onHide} size="lg" aria-labelledby="contained-modal-title-vcenter" centered backdrop="static" keyboard={false}>
      <Modal.Body className="onboard-modal">
        <h2 className="pb-20">Welcome to TBD!</h2>
        <p className="pb-20">{message}</p>
        <Link to={`/profile?reason=${profileRedirectReason.noLicense.slug}`} rel="noopener noreferrer">
          <Button className="button-green">Enter License Information</Button>
        </Link>
      </Modal.Body>
    </Modal>
  );
}

// A simple Home component that renders the dashboard along with snackbars and banners.
const Home = ({ userProfile, snackOpen, snackText, snackSeverity, handleSnackClose, bannerProps, isShowBanner }) => {
  if (!userProfile) {
    return (
      <Row className="justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Row>
    );
  }

  const bannerVariantConfig = {
    danger: { icon: <MdErrorOutline size={22} />, className: "app-banner--danger", ctaLabel: "Fix License Now", ctaTo: "/profile" },
    warning: { icon: <MdOutlineWarningAmber size={22} />, className: "app-banner--warning", ctaLabel: null, ctaTo: null },
    success: { icon: <MdCheckCircleOutline size={22} />, className: "app-banner--success", ctaLabel: null, ctaTo: null },
    info: { icon: <MdInfoOutline size={22} />, className: "app-banner--info", ctaLabel: null, ctaTo: null },
  };

  const bv = bannerVariantConfig[bannerProps.variant] || bannerVariantConfig.info;

  return (
    <>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <SnackStyleAlert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </SnackStyleAlert>
      </Snackbar>
      <div className="app-content">
        {isShowBanner && (
          <div className={`app-banner ${bv.className}`} role="alert">
            <div className="app-banner__icon">{bv.icon}</div>
            <div className="app-banner__body">
              {bannerProps.heading && <strong className="app-banner__heading">{bannerProps.heading}</strong>}
              <p className="app-banner__message">{bannerProps.message}</p>
            </div>
            {bv.ctaLabel && bv.ctaTo && (
              <Link to={bv.ctaTo} className="app-banner__cta">{bv.ctaLabel}</Link>
            )}
          </div>
        )}
        <Dashboard />
        {userProfile.hasNoLicense && <OnboardingModalOne show={true} onHide={() => {}} />}
      </div>
    </>
  );
};

/**
 * AppHeader — location-aware header placed inside <Router> so useLocation works.
 * Desktop: full horizontal nav bar (unchanged).
 * Mobile (<= 768px): logo + hamburger, with a slide-out drawer for navigation.
 */
const AppHeader = ({ loading, userRoles, logout, isExternalViewer }) => {
  const location = useLocation();
  const path = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const isActive = (...prefixes) => prefixes.some((p) => (p === "/" ? path === "/" : path.startsWith(p)));

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
    setExpandedSection(null);
  }, [path]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  if (isExternalViewer) {
    return (
      <header className="app-header">
        <div className="app-header__inner app-header__desktop">
          <span className="app-header__logo-link">
            <img className="app-header__logo" src={logo} alt={NAV_LABELS.LOGO_ALT} />
          </span>
          <Nav className="app-header__nav app-header__nav--right">
            <Nav.Item>
              <button className="app-header__link" onClick={logout} style={{ background: "none", border: "none", cursor: "pointer" }}>
                {NAV_LABELS.LOGOUT}
              </button>
            </Nav.Item>
          </Nav>
        </div>
        <div className="mob-bar">
          <span className="mob-bar__logo-link">
            <img className="mob-bar__logo" src={logo} alt={NAV_LABELS.LOGO_ALT} />
          </span>
          <button className="app-header__link" onClick={logout} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 16px" }}>
            {NAV_LABELS.LOGOUT}
          </button>
        </div>
      </header>
    );
  }

  return (
    <header className="app-header">
      {/* ═══ Desktop nav (hidden on mobile via CSS) ═══ */}
      <div className="app-header__inner app-header__desktop">
        <Link to="/" className="app-header__logo-link">
          <img className="app-header__logo" src={logo} alt={NAV_LABELS.LOGO_ALT} />
        </Link>

        <Nav className="app-header__nav app-header__nav--left">
          <Nav.Item>
            <Link className={`app-header__link${isActive("/") ? " app-header__link--active" : ""}`} to="/">
              {NAV_LABELS.HOME}
            </Link>
          </Nav.Item>
          <Nav.Item>
            <Link className={`app-header__link${isActive("/inbox", "/MessageDetails") ? " app-header__link--active" : ""}`} to="/inbox">
              {NAV_LABELS.MESSAGES}
            </Link>
          </Nav.Item>
          <NavDropdown
            title={NAV_LABELS.APPOINTMENTS}
            id="appointments-dropdown"
            className={`app-header__dropdown ${isActive("/BookingList", "/BookingCalendar", "/BookingDetails") ? "app-header__dropdown--active" : ""}`}
          >
            <NavDropdown.Item as={Link} to="/BookingList">{NAV_LABELS.FIND_APPOINTMENTS}</NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/BookingCalendar">{NAV_LABELS.CALENDAR}</NavDropdown.Item>
          </NavDropdown>
          <NavDropdown
            title={NAV_LABELS.LAB}
            id="lab-dropdown"
            className={`app-header__dropdown ${isActive("/request", "/result", "/manage-lab-order", "/shipping-manifest", "/userrequest") ? "app-header__dropdown--active" : ""}`}
          >
            <NavDropdown.Item as={Link} to="/request">{NAV_LABELS.LAB_REQUESTS}</NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/result">{NAV_LABELS.LAB_RESULTS}</NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/manage-lab-order">{NAV_LABELS.MANAGE_LAB_ORDER}</NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/shipping-manifest">{NAV_LABELS.SHIPPING_MANIFEST}</NavDropdown.Item>
          </NavDropdown>
        </Nav>

        <Nav className="app-header__nav app-header__nav--right">
          <NavDropdown
            title={NAV_LABELS.SEARCH}
            id="search-dropdown"
            className={`app-header__dropdown ${isActive("/SearchPatient", "/PatientProfile", "/OrderKitLookup") ? "app-header__dropdown--active" : ""}`}
          >
            <NavDropdown.Item as={Link} to="/SearchPatient">{NAV_LABELS.PATIENT_SEARCH}</NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/OrderKitLookup">{NAV_LABELS.ORDER_KIT_LOOKUP}</NavDropdown.Item>
          </NavDropdown>
          <NavDropdown title={NAV_LABELS.ACCOUNT} id="account-dropdown" alignRight className="app-header__dropdown">
            {!loading && userRoles.includes(ROLE_NAME.SUPER_ADMIN) && (
              <>
                <NavDropdown.Item as={Link} to="/super-admin">{NAV_LABELS.SUPER_ADMIN}</NavDropdown.Item>
                <NavDropdown.Divider />
              </>
            )}
            <NavDropdown.Item as={Link} to="/profile">{NAV_LABELS.MANAGE_PROFILE}</NavDropdown.Item>
            <NavDropdown.Divider />
            <NavDropdown.Item as={Link} onClick={logout}>{NAV_LABELS.LOGOUT}</NavDropdown.Item>
          </NavDropdown>
        </Nav>
      </div>

      {/* ═══ Mobile top bar (hidden on desktop via CSS) ═══ */}
      <div className="mob-bar">
        <Link to="/" className="mob-bar__logo-link">
          <img className="mob-bar__logo" src={logo} alt={NAV_LABELS.LOGO_ALT} />
        </Link>
        <button
          className="mob-bar__toggle"
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
        </button>
      </div>

      {/* ═══ Mobile drawer overlay ═══ */}
      {mobileOpen && <div className="mob-overlay" onClick={() => setMobileOpen(false)} />}

      {/* ═══ Mobile drawer ═══ */}
      <nav className={`mob-drawer ${mobileOpen ? "mob-drawer--open" : ""}`} aria-label="Mobile navigation">
        {/* Primary links */}
        <div className="mob-drawer__section">
          <Link className={`mob-drawer__link ${isActive("/") ? "mob-drawer__link--active" : ""}`} to="/">
            {NAV_LABELS.HOME}
          </Link>
          <Link className={`mob-drawer__link ${isActive("/inbox", "/MessageDetails") ? "mob-drawer__link--active" : ""}`} to="/inbox">
            {NAV_LABELS.MESSAGES}
          </Link>
          <Link className={`mob-drawer__link ${isActive("/SearchPatient", "/PatientProfile") ? "mob-drawer__link--active" : ""}`} to="/SearchPatient">
            {NAV_LABELS.PATIENT_SEARCH}
          </Link>
          <Link className={`mob-drawer__link ${isActive("/OrderKitLookup") ? "mob-drawer__link--active" : ""}`} to="/OrderKitLookup">
            {NAV_LABELS.ORDER_KIT_LOOKUP}
          </Link>
        </div>

        <div className="mob-drawer__divider" />

        {/* Appointments (expandable) */}
        <div className="mob-drawer__section">
          <button
            className={`mob-drawer__link mob-drawer__link--parent ${isActive("/BookingList", "/BookingCalendar", "/BookingDetails") ? "mob-drawer__link--active" : ""}`}
            onClick={() => toggleSection("appointments")}
            aria-expanded={expandedSection === "appointments"}
          >
            {NAV_LABELS.APPOINTMENTS}
            <BsChevronRight className={`mob-drawer__chevron ${expandedSection === "appointments" ? "mob-drawer__chevron--open" : ""}`} />
          </button>
          {expandedSection === "appointments" && (
            <div className="mob-drawer__sub">
              <Link className="mob-drawer__sublink" to="/BookingList">{NAV_LABELS.FIND_APPOINTMENTS}</Link>
              <Link className="mob-drawer__sublink" to="/BookingCalendar">{NAV_LABELS.CALENDAR}</Link>
            </div>
          )}

          {/* Lab (expandable) */}
          <button
            className={`mob-drawer__link mob-drawer__link--parent ${isActive("/request", "/result", "/manage-lab-order", "/shipping-manifest", "/userrequest") ? "mob-drawer__link--active" : ""}`}
            onClick={() => toggleSection("lab")}
            aria-expanded={expandedSection === "lab"}
          >
            {NAV_LABELS.LAB}
            <BsChevronRight className={`mob-drawer__chevron ${expandedSection === "lab" ? "mob-drawer__chevron--open" : ""}`} />
          </button>
          {expandedSection === "lab" && (
            <div className="mob-drawer__sub">
              <Link className="mob-drawer__sublink" to="/request">{NAV_LABELS.LAB_REQUESTS}</Link>
              <Link className="mob-drawer__sublink" to="/result">{NAV_LABELS.LAB_RESULTS}</Link>
              <Link className="mob-drawer__sublink" to="/manage-lab-order">{NAV_LABELS.MANAGE_LAB_ORDER}</Link>
              <Link className="mob-drawer__sublink" to="/shipping-manifest">{NAV_LABELS.SHIPPING_MANIFEST}</Link>
            </div>
          )}
        </div>

        <div className="mob-drawer__divider" />

        {/* Account section */}
        <div className="mob-drawer__section">
          <button
            className="mob-drawer__link mob-drawer__link--parent"
            onClick={() => toggleSection("account")}
            aria-expanded={expandedSection === "account"}
          >
            {NAV_LABELS.ACCOUNT}
            <BsChevronRight className={`mob-drawer__chevron ${expandedSection === "account" ? "mob-drawer__chevron--open" : ""}`} />
          </button>
          {expandedSection === "account" && (
            <div className="mob-drawer__sub">
              {!loading && userRoles.includes(ROLE_NAME.SUPER_ADMIN) && (
                <Link className="mob-drawer__sublink" to="/super-admin">{NAV_LABELS.SUPER_ADMIN}</Link>
              )}
              <Link className="mob-drawer__sublink" to="/profile">{NAV_LABELS.MANAGE_PROFILE}</Link>
              <button className="mob-drawer__sublink mob-drawer__sublink--danger" onClick={logout}>
                {NAV_LABELS.LOGOUT}
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

const Navigation = () => {
  const history = useHistory();
  const [clinicianId, setClinicianId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState("/");
  const [bannerProps, setBannerProps] = useState({});
  const [isShowBanner, setIsShowBanner] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [preReqMet, setPreReqMet] = useState(false);

  // Remove any unwanted CSS classes only once when mounting.
  useEffect(() => {
    removeClass();
  }, []);

  // Memoized snack handlers
  const handleSnackOpen = useCallback((text, severity) => {
    setSnackSeverity(severity);
    setSnackText(text);
    setSnackOpen(true);
  }, []);

  const handleSnackClose = useCallback(() => {
    setSnackOpen(false);
  }, []);

  /**
   * Secure logout function
   * SECURITY: Clears all auth state and Cognito cookies
   */
  const logout = useCallback(async () => {
    try {
      await Auth.signOut();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear any Cognito cookies that may exist
      clearCognitoCookies();
      window.location.reload();
    }
  }, []);

  // One-shot guard: React.StrictMode double-invokes effects in dev. Without this, the
  // initialize below would fire twice, producing duplicate /profile GETs (and, on 404,
  // duplicate /profile/create POSTs). The backend is idempotent, but we keep logs clean.
  const initRanRef = useRef(false);

  // Initialize authentication, load profile, and user roles.
  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;

    /**
     * Fetch the clinician profile; if the backend returns 404 (no DynamoDB row for this
     * user), invoke the create endpoint once and re-fetch. This self-heals accounts whose
     * profile creation failed at signup time.
     *
     * - Only 404 triggers recovery. 401/403/5xx/network errors rethrow so existing error
     *   handling surfaces them unchanged.
     * - `id` and `email` are defensive diagnostics: the backend derives identity from the
     *   JWT regardless, but we refuse to POST without both present to avoid ever sending
     *   a misdirected create.
     */
    const fetchProfileWithRecovery = async ({ id, email }) => {
      try {
        return await ApiService.getClinicianProfile();
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
        if (!id || !email) throw err;
        console.info("Clinician profile missing on login; attempting recovery create");
        await ApiService.addClinicianProfileOnSignup({ id, email });
        return await ApiService.getClinicianProfile();
      }
    };

    const initialize = async () => {
      try {
        setLoading(true);
        const id = await clinicianIdFromRefreshAuth();
        setClinicianId(id);
        const roles = await getUserRoles();
        setUserRoles(roles);

        const isViewerOnly = roles.length === 1 && roles.includes(ROLE_NAME.PREP_DASHBOARD_VIEWER);
        if (isViewerOnly) {
          setPreReqMet(true);
          return;
        }

        const decodedIdToken = await decodeIdToken();
        const email = decodedIdToken?.email ?? null;

        const profileResponse = await fetchProfileWithRecovery({ id, email });
        if (profileResponse?.data?.status === CLINICIAN_STATUS.INACTIVE) {
          handleSnackOpen("Your account is disabled, please contact support", "error");
          logout();
          return;
        }
        setUserProfile(profileResponse.data);
      } catch (err) {
        console.error(err);
        handleSnackOpen(`Initialization failed: ${err?.message ?? "unknown cause"}`, "error");
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [handleSnackOpen, logout]);

  // Update UI state based on profile requirements.
  useEffect(() => {
    if (!userProfile) {
      setBannerProps({
        heading: "Clinician Profile not found",
        message: "Please contact us to resolve this issue",
        variant: "danger"
      });
      setIsShowBanner(true);
      return;
    }

    if (!userRoles?.length) {
      setBannerProps({
        heading: "You have not been assigned any roles",
        message: "Please contact us to resolve this issue",
        variant: "danger"
      });
      setIsShowBanner(true);
      return;
    }

    setPreReqMet(true);
    // setBannerProps({
    //   heading: "Access restricted",
    //   message: `Your profile is missing license info, please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update`,
    //   variant: "danger"
    // });
    // return

    if (userRoles.includes(ROLE_NAME.CLINICIAN)) {
      if (userProfile.hasNoLicense) {
        setBannerProps({
          heading: "No License have been added",
          message: `Your profile is missing license info, please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update`,
          variant: "danger"
        });
        setIsShowBanner(true);
        // Do not redirect; the onboarding modal will prompt for license info.
        return;
      }

      if (!userProfile.hasCalendar) {
        setBannerProps({
          heading: "Calendar link missing",
          message: `Your profile is missing calendar link, please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update`,
          variant: "danger"
        });
        setIsShowBanner(true);
        setShouldRedirect(true);
        setRedirectPath(`/profile?reason=${profileRedirectReason.invalidOrNoCalendar.slug}`);
        return;
      }

      if (userProfile?.hasExpiredOrInValidLicense) {
        const invalidStates = Array.isArray(userProfile?.inValidLicenseStates) ? userProfile.inValidLicenseStates.filter(Boolean) : [];

        const invalidStatesDisplay = invalidStates.length ? ` for the following state(s): ${invalidStates.join(", ")}` : "";

        setBannerProps({
          heading: "Invalid or expired licenses",
          message: `Your profile has invalid or expired licenses${invalidStatesDisplay}. Please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update.`,
          variant: "danger"
        });

        setIsShowBanner(true);
        // setShouldRedirect(true);
        // setRedirectPath(`/profile?reason=${profileRedirectReason.expiredOrInvalidLicense.slug}`);
        return;
      }
    }

    const restrictedRoles = [ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT, ROLE_NAME.AUDITOR_READ_ONLY];
    const hasOnlyRestrictedRole = Array.isArray(userRoles) && userRoles.length > 0 &&
      userRoles.every(role => restrictedRoles.includes(role));

    if (hasOnlyRestrictedRole) {
      setBannerProps({
        heading: `Access is restricted`,
        message: "You do not have access to clinical operations in the EMR Dashboard",
        variant: "warning"
      });
      setIsShowBanner(true);
      setPreReqMet(true);
      return;
    }

    if (userRoles.includes(ROLE_NAME.SUPER_ADMIN)) {
      setBannerProps({
        heading: "WELCOME, SUPER ADMIN",
        message: "You have full access to all features",
        variant: "success"
      });
      setPreReqMet(true);
      return;
    }

    // All prerequisites met.
    setPreReqMet(true);
  }, [userProfile, userRoles]);

  const isExternalViewer = userRoles.length === 1 && userRoles.includes(ROLE_NAME.PREP_DASHBOARD_VIEWER);

  if (loading) {
    return (
      <Row className="justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Row>
    );
  }

  if (isExternalViewer) {
    return (
      <Router>
        <AppHeader loading={loading} userRoles={userRoles} logout={logout} isExternalViewer />
        <Switch>
          <Route exact path={url.prepDashboard}>
            <div className="app-content" style={{ paddingTop: 16 }}>
              <PrepDashboard resetView={() => {}} externalView />
            </div>
          </Route>
          <Redirect to={url.prepDashboard} />
        </Switch>
      </Router>
    );
  }

  return (
    <Router>
      {/* If prerequisites aren’t met, perform redirection */}
      {shouldRedirect && <Redirect to={redirectPath} />}
      <AppHeader loading={loading} userRoles={userRoles} logout={logout} isExternalViewer={false} />

      <Switch>
        <AuthWrapper>
          {preReqMet && (
            <>
              <PrivateRoute exact path={url.admin} component={AdminDashboard} allowedRoles={[ROLE_NAME.SUPER_ADMIN]} />
              <PrivateRoute
                exact
                path={url.inbox}
                component={Inbox}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path={url.inboxItem}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/MessageDetails"
                component={MessageView}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/request"
                component={LabRequest}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/manage-lab-order"
                component={ManageLabOrder}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/shipping-manifest"
                component={ShippingManifest}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/result"
                component={LabResult}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/result/:kitid"
                component={UserResult}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/userrequest/:kitid"
                component={UserRequest}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/report/orders"
                component={ReportOrders}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/BookingList"
                component={BookingList}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/BookingCalendar"
                component={BookingCalendar}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/BookingDetails"
                component={BookingDetails}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/KitDetails"
                component={KitDetailsPage}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/SearchPatient"
                component={PatientSearchPage}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/OrderKitLookup"
                component={OrderKitLookupPage}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/PatientProfile"
                component={PatientProfile}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/Payment"
                component={CollectPayment}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
            </>
          )}
          <Route path="/license" component={() => <Profile isGoLicenseView />} />
          <Route path="/profile" component={Profile} />
          <Route path="/calendar" component={() => <Profile isGoCalendarView />} />
          <Route exact path="/">
            <Home
              userProfile={userProfile}
              snackOpen={snackOpen}
              snackText={snackText}
              snackSeverity={snackSeverity}
              handleSnackClose={handleSnackClose}
              bannerProps={bannerProps}
              isShowBanner={isShowBanner}
            />
          </Route>
          <Route path="/401-page">
            <Unauthorized userProfile={userProfile} />
          </Route>
        </AuthWrapper>
      </Switch>
    </Router>
  );
};

export default Navigation;
